import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Manages Web Push subscription lifecycle:
 *   1. Registers /service-worker.js
 *   2. Fetches VAPID public key from the backend
 *   3. Subscribes / unsubscribes through the browser Push API
 *   4. Persists the subscription to the backend
 *
 * Returns { supported, subscribed, loading, error, subscribe, unsubscribe }
 */
export function usePushNotifications() {
  const [supported,   setSupported]   = useState(false);
  const [subscribed,  setSubscribed]  = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [registration, setRegistration] = useState(null);

  // Register the service worker once and check existing subscription
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setSupported(true);

    navigator.serviceWorker
      .register('/service-worker.js')
      .then(reg => {
        setRegistration(reg);
        return reg.pushManager.getSubscription();
      })
      .then(sub => {
        if (sub) setSubscribed(true);
      })
      .catch(err => {
        console.error('[usePushNotifications] SW registration failed:', err);
        setError('Service worker registration failed.');
      });
  }, []);

  const subscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Notification permission denied.');
        return;
      }

      const { data } = await axios.get('/api/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

      const reg = registration ?? await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      const json = sub.toJSON();
      await axios.post('/api/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
      });

      setSubscribed(true);
    } catch (err) {
      console.error('[usePushNotifications] Subscribe failed:', err);
      setError('Failed to enable notifications.');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const reg = registration ?? await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post('/api/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error('[usePushNotifications] Unsubscribe failed:', err);
      setError('Failed to disable notifications.');
    } finally {
      setLoading(false);
    }
  };

  return { supported, subscribed, loading, error, subscribe, unsubscribe };
}

// Converts a base64url VAPID public key to the Uint8Array that the
// browser's pushManager.subscribe() expects.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}
